import {
  CheckboxGroup as HeroUICheckboxGroup,
  CheckboxGroupProps as HeroUICheckboxGroupProps,
} from "@heroui/react";
import clsx from "clsx";
import { getKey } from "core/model/utils/strings";
import { useTranslations } from "translations";
import { Checkbox, CheckboxProps } from "../checkbox/checkbox";

export type CheckboxGroupOption<T = string | number> = Omit<
  CheckboxProps,
  "name" | "id" | "value"
> & {
  subForm?: (props: {
    isDisabled?: boolean;
    size?: CheckboxProps["size"];
  }) => React.ReactNode;
  value: T;
};

export type CheckboxGroupProps<T = string | number> = Omit<
  HeroUICheckboxGroupProps,
  | "children"
  | "classNames"
  | "color"
  | "name"
  | "onChange"
  | "onValueChange"
  | "options"
  | "size"
  | "value"
> & {
  color?: CheckboxProps["color"];
  name: string;
  onChange?: (value: T[]) => void;
  onValueChange?: (value: T[]) => void;
  options?: CheckboxGroupOption<T>[];
  size?: CheckboxProps["size"];
  value?: T[];
};

export function CheckboxGroup<T = string | number>({
  onChange,
  onValueChange,
  options,
  size = "md",
  value,
  ...props
}: CheckboxGroupProps<T>) {
  const translations = useTranslations();
  // Handle value changes - call both form handler and optional onChange
  const handleValueChange = (newValue: string[]) => {
    if (!options?.length) return;

    // Convert string values back to original typed values
    const typedValues: T[] = [];
    for (const stringVal of newValue) {
      const option = options.find((opt) => String(opt.value) === stringVal);
      if (option) {
        typedValues.push(option.value);
      }
    }

    onValueChange?.(typedValues);
    onChange?.(typedValues);
  };

  // Convert current typed values to strings for HeroUI
  const stringValues = value?.map((val) => String(val));

  return (
    <HeroUICheckboxGroup
      {...props}
      classNames={{
        base: "leading-inherit",
        description: "text-gray-600",
        errorMessage: "text-danger-dark text-xs",
        label: clsx(
          "text-base leading-inherit font-semibold text-default-900 after:text-gray-900",
          {
            "text-sm": size === "sm" || size === "md",
          },
        ),
      }}
      data-testid={props.name}
      size={size}
      value={stringValues}
      onValueChange={handleValueChange}
    >
      {options?.map((option, i) => {
        const { subForm, ...checkboxProps } = option;
        const isSelected = !!(option.value && value?.includes(option.value));
        const subFormId = `${props.name}-${option.value}-subform`;

        return (
          <div
            key={getKey(String(option.value), i)}
            className="flex flex-col gap-3"
          >
            <Checkbox
              key={getKey(String(option.value), i)}
              {...checkboxProps}
              aria-controls={subForm ? subFormId : undefined}
              aria-expanded={!!subForm && isSelected}
              isDisabled={props.isDisabled || option.isDisabled}
              name={props.name}
              size={size}
              value={String(option.value)}
            />
            {subForm && (
              <>
                <div
                  className={clsx("mb-2 pl-6 transition-all duration-200", {
                    "hidden opacity-0": !isSelected,
                    "opacity-100": isSelected,
                  })}
                  id={subFormId}
                  role="group"
                >
                  {subForm({
                    isDisabled: props.isDisabled,
                    size,
                  })}
                </div>
                {/* TODO: Screen reader announcement that sub form has been collapsed, should be using a11y hook: https://recare.atlassian.net/browse/DEV-17121 */}
                <span aria-live="polite" className="sr-only">
                  {isSelected
                    ? translations.accessibility.subFormExpanded
                    : translations.accessibility.subFormCollapsed}
                </span>
              </>
            )}
          </div>
        );
      })}
    </HeroUICheckboxGroup>
  );
}

CheckboxGroup.displayName = "RecareUI.CheckboxGroup";
